import { ReactNode, useEffect, useState } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { legalService } from '@/services/legal.service'
import { TermsAcceptanceModal } from './TermsAcceptanceModal'
import { Loader2 } from 'lucide-react'

/**
 * TermsGuard — wrappa le rotte protette (es. /trading/*).
 * Se l'utente NON è admin e non ha accettato la versione corrente del contratto,
 * mostra il modale di accettazione al posto della pagina.
 *
 * Logica:
 * 1. Admin → bypass totale.
 * 2. Se i campi terms_accepted_* dell'auth store mancano (sessione vecchia),
 *    chiama /legal/terms-status per ottenere lo stato e la versione corrente.
 * 3. Se non accettato → modale full-screen non chiudibile.
 */
export function TermsGuard({ children }: { children: ReactNode }) {
  const { user, updateUser } = useAuthStore()
  const isAdmin = !!user?.is_admin

  const [currentVersion, setCurrentVersion] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (isAdmin || !user) {
      setChecked(true)
      return
    }
    let alive = true
    legalService
      .getStatus()
      .then((s) => {
        if (!alive) return
        setCurrentVersion(s.current_version)
        // Allinea lo store coi dati freschi dal server
        if (s.accepted) {
          updateUser({
            terms_accepted_at: s.accepted_at,
            terms_version_accepted: s.accepted_version,
          })
        }
        setChecked(true)
      })
      .catch(() => {
        if (!alive) return
        setChecked(true)
      })
    return () => {
      alive = false
    }
  }, [user?.id, isAdmin])

  if (isAdmin) return <>{children}</>

  if (!checked) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Verifica termini di servizio...
      </div>
    )
  }

  const acceptedVersion = user?.terms_version_accepted ?? null
  const accepted =
    !!user?.terms_accepted_at &&
    !!currentVersion &&
    acceptedVersion === currentVersion

  if (accepted) return <>{children}</>

  return (
    <TermsAcceptanceModal
      onAccepted={() => {
        // dopo accept → ricarica la pagina corrente, in modo che le call /api siano riprovate
        window.location.reload()
      }}
    />
  )
}
